import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { config } from "../config";
import type { RequestSummary } from "../api/requests";
import { getEvents, safeCapture } from "../analytics";
import { upsertRequestInList } from "../utils/upsertRequestInList";

export { upsertRequestInList };

/** Statuses past which a money request no longer changes (SSE can stop). */
const TERMINAL_STATUSES = new Set<RequestSummary["status"]>([
  "funded",
  "failed",
  "expired",
  "cancelled",
]);

export function isRequestNonTerminal(req: RequestSummary): boolean {
  return !TERMINAL_STATUSES.has(req.status);
}

export function hasNonTerminalRequests(reqs: RequestSummary[]): boolean {
  return reqs.some(isRequestNonTerminal);
}

function closeEventSource(
  es: EventSource | null,
  esRef: MutableRefObject<EventSource | null>
): void {
  if (!es) return;
  es.close();
  if (esRef.current === es) {
    esRef.current = null;
  }
}

export interface UseRequestSSEOptions {
  /** If set, only stream while this request exists in the list and is non-terminal. */
  watchRequestId?: string;
}

/**
 * Keeps money-request statuses live via Server-Sent Events.
 *
 * Mirrors fund-request's `useOperationSSE`: the caller owns the requests list
 * and provides an `onUpsert` callback. The stream opens once at least one loaded
 * request is non-terminal (or, in watch mode, once the watched request is loaded
 * and non-terminal) and re-opens when that becomes true again after closing.
 */
export function useRequestSSE(
  linkId: string | null | undefined,
  requests: RequestSummary[],
  onUpsert: (req: RequestSummary) => void,
  onReconnect?: () => void,
  options?: UseRequestSSEOptions
): void {
  const watchRequestId = options?.watchRequestId;
  const esRef = useRef<EventSource | null>(null);
  const isFirstOpenRef = useRef(true);

  const requestsRef = useRef(requests);
  const onUpsertRef = useRef(onUpsert);
  const onReconnectRef = useRef(onReconnect);
  requestsRef.current = requests;
  onUpsertRef.current = onUpsert;
  onReconnectRef.current = onReconnect;

  const hasActiveRequests = useMemo(() => {
    if (!watchRequestId) {
      return hasNonTerminalRequests(requests);
    }
    const req = requests.find((r) => r.requestId === watchRequestId);
    return req ? isRequestNonTerminal(req) : false;
  }, [requests, watchRequestId]);

  useEffect(() => {
    if (!linkId || !hasActiveRequests) {
      closeEventSource(esRef.current, esRef);
      return;
    }

    if (esRef.current) return;

    isFirstOpenRef.current = true;
    const url = `${config.API_BASE_URL}/widget/links/${linkId}/requests/sse`;
    const es = new EventSource(url);
    esRef.current = es;

    const maybeClose = (incoming?: RequestSummary): void => {
      const reqs = incoming
        ? upsertRequestInList(requestsRef.current, incoming)
        : requestsRef.current;

      if (watchRequestId) {
        const req = reqs.find((r) => r.requestId === watchRequestId);
        if (!req || !isRequestNonTerminal(req)) {
          closeEventSource(es, esRef);
        }
        return;
      }
      if (!hasNonTerminalRequests(reqs)) {
        closeEventSource(es, esRef);
      }
    };

    es.onopen = () => {
      if (!isFirstOpenRef.current) {
        onReconnectRef.current?.();
      }
      isFirstOpenRef.current = false;
    };

    es.onmessage = (event: MessageEvent) => {
      try {
        const raw = event.data as string;
        const data = typeof raw === "string" ? JSON.parse(raw) : raw;

        if (data?.type === "heartbeat") {
          maybeClose();
          return;
        }

        const updated = data as RequestSummary;
        if (!updated?.requestId) return;

        if (watchRequestId && updated.requestId !== watchRequestId) {
          maybeClose();
          return;
        }

        safeCapture(getEvents().REQUEST_STATUS_CHANGED, {
          request_id: updated.requestId,
          status: updated.status,
          operation_id: updated.operationId,
          amount: updated.amount,
          operation_status: updated.operation?.status,
          funding_status: updated.operation?.fundingStatus,
        });

        onUpsertRef.current(updated);
        maybeClose(updated);
      } catch (e) {
        console.error("[useRequestSSE] message parse error", e);
      }
    };

    es.onerror = () => {
      console.warn("[useRequestSSE] connection issue (auto-retry)", linkId);
    };

    return () => {
      closeEventSource(es, esRef);
    };
  }, [linkId, hasActiveRequests, watchRequestId]);
}
